import React, { useState } from "react";
import { useSelector } from "react-redux";
import { selectCid } from "../../../../features/companyid";
import { COMPONENT_TYPES } from "../../../../pages/constants/AiAgent";
import { useComponentAction } from "./useComponentAction";
import useComponentSearch from "./useComponentSearch";
import Button from "../../../ui/Button";
import { IoMdClose } from "react-icons/io";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const CompanyRow = ({ company, priorityOptions, scopeOptions, onChange, onRemove, disabled }) => (
  <div className="flex items-center gap-2 border border-gray-200 rounded-lg p-2 bg-white">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-primaryBlack truncate">
        {company.name}
      </p>
      <p className="text-[10px] text-textGray">ID: {company.id}</p>
    </div>
    <select
      value={company.priority}
      onChange={(e) => onChange(company.id, "priority", e.target.value)}
      disabled={disabled}
      className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-gray-50 focus:outline-none focus:border-blue-400"
    >
      {priorityOptions.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
    <select
      value={company.scope}
      onChange={(e) => onChange(company.id, "scope", e.target.value)}
      disabled={disabled}
      className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-gray-50 focus:outline-none focus:border-blue-400"
    >
      {scopeOptions.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
    {!disabled && (
      <button
        onClick={() => onRemove(company.id)}
        className="text-gray-400 hover:text-red-500 flex-shrink-0 p-1"
      >
        <IoMdClose size={14} />
      </button>
    )}
  </div>
);

const CompanyFilter = ({ payload, disabled }) => {
  const { text, data } = payload;
  const searchEndpoint = data?.search_endpoint || "";
  const priorityOptions = data?.priority_options || [
    { value: "CAN_HAVE", label: "Can Have" },
    { value: "MUST_HAVE", label: "Must Have" },
    { value: "DOESNT_HAVE", label: "Doesn't Have" },
  ];
  const scopeOptions = data?.scope_options || [
    { value: "PAST", label: "Past" },
    { value: "CURRENT_OR_PAST", label: "Current or Past" },
    { value: "PAST_NOT_CURRENT", label: "Past (Not Current)" },
  ];
  const defaultPriority = data?.default_priority || "CAN_HAVE";
  const defaultScope = data?.default_scope || "CURRENT_OR_PAST";

  const companyId = useSelector(selectCid);
  const { sendAction } = useComponentAction();

  const initialSelected = () => {
    if (data?.selected_companies?.length > 0) {
      return data.selected_companies.map((item) => ({
        id: item.id,
        name: item.name || item.id,
        priority: item.priority || defaultPriority,
        scope: item.scope || defaultScope,
      }));
    }
    return [];
  };

  const [selected, setSelected] = useState(initialSelected);
  const [submitted, setSubmitted] = useState(disabled);
  const wasSkipped = disabled && selected.length === 0 && data?.skipped;

  const { query, results, isLoading, handleQueryChange, clearSearch } =
    useComponentSearch(searchEndpoint, companyId);

  const handleSelect = (item) => {
    if (selected.find((s) => s.id === item.id)) return;
    setSelected((prev) => [
      ...prev,
      {
        id: item.id,
        name: item.name,
        priority: defaultPriority,
        scope: defaultScope,
      },
    ]);
    clearSearch();
  };

  const handleRemove = (id) => {
    setSelected((prev) => prev.filter((s) => s.id !== id));
  };

  const handleChange = (id, field, value) => {
    setSelected((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s))
    );
  };

  const handleConfirm = () => {
    setSubmitted(true);
    sendAction(COMPONENT_TYPES.COMPANY_FILTER, "confirm_company_filter", {
      companies: selected.map(({ id, name, priority, scope }) => ({
        id,
        name,
        priority,
        scope,
      })),
    });
  };

  const handleSkip = () => {
    setSubmitted(true);
    sendAction(COMPONENT_TYPES.COMPANY_FILTER, "skip_company_filter", {});
  };

  const isSelected = (id) => selected.some((s) => s.id === id);

  return (
    <div className="flex flex-col gap-3 w-full">
      {text && (
        <p className="text-sm text-gray-700 whitespace-pre-line">{text}</p>
      )}

      {/* Skipped indicator */}
      {wasSkipped && (
        <p className="text-xs text-gray-400 italic">Skipped</p>
      )}

      {/* Search input */}
      {!submitted && (
        <>
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              placeholder="Search companies..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-gray-50"
            />
            {isLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <AiOutlineLoading3Quarters className="w-4 h-4 text-gray-400 animate-spin" />
              </div>
            )}
          </div>

          {/* Dropdown results */}
          {query.length >= 2 && !isLoading && results.length === 0 && (
            <p className="text-xs text-gray-400 px-1">No companies found</p>
          )}

          {results.length > 0 && (
            <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto bg-white">
              {results.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  disabled={isSelected(item.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors flex items-center justify-between ${
                    isSelected(item.id)
                      ? "text-gray-400 bg-gray-50 cursor-default"
                      : "text-gray-700"
                  }`}
                >
                  <span className="truncate">{item.name}</span>
                  {isSelected(item.id) && (
                    <span className="text-xs text-blue-500 flex-shrink-0 ml-2">
                      selected
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Selected company rows */}
      {selected.length > 0 && (
        <div className="flex flex-col gap-2">
          {selected.map((company) => (
            <CompanyRow
              key={company.id}
              company={company}
              priorityOptions={priorityOptions}
              scopeOptions={scopeOptions}
              onChange={handleChange}
              onRemove={handleRemove}
              disabled={submitted}
            />
          ))}
        </div>
      )}

      {/* Action buttons */}
      {!submitted && (
        <div className="flex items-center justify-between mt-1">
          <Button
            variant="secondary"
            size="sm"
            className="!text-xs"
            onClick={handleSkip}
          >
            Skip
          </Button>
          <Button
            variant="black"
            size="sm"
            className="!text-xs"
            onClick={handleConfirm}
            disabled={selected.length === 0}
          >
            Confirm ({selected.length} selected)
          </Button>
        </div>
      )}
    </div>
  );
};

export default CompanyFilter;
